/**
 * Customer service for managing customer data
 */
import StorageService from './StorageService';
import Customer from '../models/Customer';

class CustomerService {
  /**
   * Constructor
   */
  constructor() {
    this.storageService = new StorageService('customers');
  }

  /**
   * Get all customers
   * @returns {Array} All customers
   */
  getAllCustomers() {
    return this.storageService.getAllAsArray().map(customer => 
      Customer.fromJSON(customer)
    );
  }

  /**
   * Get customer by ID
   * @param {string} id - Customer ID
   * @returns {Customer|null} Customer or null if not found
   */
  getCustomerById(id) {
    const customerData = this.storageService.getById(id);
    if (!customerData) return null;
    
    return Customer.fromJSON(customerData);
  }

  /**
   * Create customer
   * @param {Object} customerData - Customer data
   * @returns {Customer} Created customer
   */
  createCustomer(customerData) {
    const customer = new Customer(customerData);
    this.storageService.save(customer.id, customer.toJSON());
    return customer;
  }

  /**
   * Update customer
   * @param {string} id - Customer ID
   * @param {Object} customerData - Customer data to update
   * @returns {Customer|null} Updated customer or null if not found
   */
  updateCustomer(id, customerData) {
    const existingCustomerData = this.storageService.getById(id);
    if (!existingCustomerData) return null;
    
    const customer = Customer.fromJSON(existingCustomerData);
    customer.update(customerData);
    
    this.storageService.save(customer.id, customer.toJSON());
    return customer;
  }

  /**
   * Delete customer
   * @param {string} id - Customer ID
   * @returns {boolean} Whether deletion was successful
   */
  deleteCustomer(id) {
    return this.storageService.delete(id);
  }

  /**
   * Search customers by text query
   * @param {string} query - Search query
   * @returns {Array} Matching customers
   */
  searchCustomers(query) {
    if (!query) return this.getAllCustomers();
    
    const lowercasedQuery = query.toLowerCase();
    
    return this.storageService.find(customer => {
      // Search in various fields
      return (
        customer.name?.toLowerCase().includes(lowercasedQuery) ||
        customer.phoneNumber?.includes(lowercasedQuery) ||
        customer.phone?.includes(lowercasedQuery) ||
        customer.email?.toLowerCase().includes(lowercasedQuery) ||
        customer.wechatId?.toLowerCase().includes(lowercasedQuery) ||
        customer.notes?.toLowerCase().includes(lowercasedQuery)
      );
    }).map(customer => Customer.fromJSON(customer));
  }

  /**
   * Filter customers by tags
   * @param {Array<string>} tags - Tags to filter by
   * @returns {Array} Customers with any of the specified tags
   */
  filterByTags(tags) {
    if (!tags || tags.length === 0) return this.getAllCustomers();
    
    return this.storageService.find(customer => {
      if (!customer.tags) return false;
      return tags.some(tag => customer.tags.includes(tag));
    }).map(customer => Customer.fromJSON(customer));
  }

  /**
   * Get all unique tags from all customers
   * @returns {Array<string>} All unique tags
   */
  getAllTags() {
    const tagsSet = new Set();
    
    this.getAllCustomers().forEach(customer => {
      if (customer.tags && Array.isArray(customer.tags)) {
        customer.tags.forEach(tag => tagsSet.add(tag));
      }
    });
    
    return Array.from(tagsSet).sort();
  }

  /**
   * Record a visit for a customer
   * @param {string} id - Customer ID
   * @param {Date|string} [visitDate] - Visit date
   * @returns {Customer|null} Updated customer or null if not found
   */
  recordCustomerVisit(id, visitDate = new Date()) {
    const customer = this.getCustomerById(id);
    if (!customer) return null;
    
    customer.recordVisit(visitDate);
    this.storageService.save(customer.id, customer.toJSON());
    
    return customer;
  }

  /**
   * Add medical condition to customer history
   * @param {string} id - Customer ID
   * @param {string} condition - Medical condition
   * @param {string} details - Condition details
   * @param {Date|string} [diagnosisDate] - Date of diagnosis
   * @returns {Customer|null} Updated customer or null if not found
   */
  addMedicalCondition(id, condition, details, diagnosisDate = null) {
    const customer = this.getCustomerById(id);
    if (!customer) return null;
    
    customer.addMedicalCondition(condition, details, diagnosisDate);
    this.storageService.save(customer.id, customer.toJSON());
    
    return customer;
  }

  /**
   * Add allergy to customer
   * @param {string} id - Customer ID
   * @param {string} allergen - Allergen name
   * @param {string} [reaction] - Allergic reaction details
   * @param {string} [severity] - Severity level
   * @returns {Customer|null} Updated customer or null if not found
   */
  addAllergy(id, allergen, reaction = '', severity = 'moderate') {
    const customer = this.getCustomerById(id);
    if (!customer) return null;
    
    customer.addAllergy(allergen, reaction, severity);
    this.storageService.save(customer.id, customer.toJSON());
    
    return customer;
  }

  /**
   * Get customers with recent visits
   * @param {number} days - Number of days to look back
   * @returns {Array} Customers with recent visits
   */
  getRecentVisits(days = 30) {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return this.storageService.find(customer => {
      if (!customer.lastVisitDate) return false;
      return new Date(customer.lastVisitDate) >= cutoffDate;
    }).map(customer => Customer.fromJSON(customer));
  }

  /**
   * Get customers with birthdays in the specified month
   * @param {number} [month] - Month (1-12), defaults to current month
   * @returns {Array} Customers with birthdays in the month
   */
  getCustomersBirthdaysInMonth(month = null) {
    // Default to current month
    const targetMonth = month || new Date().getMonth() + 1;
    
    return this.storageService.find(customer => {
      if (!customer.birthdate) return false;
      const birthDate = new Date(customer.birthdate);
      return birthDate.getMonth() + 1 === targetMonth;
    }).map(customer => Customer.fromJSON(customer));
  }

  /**
   * Get total number of customers
   * @returns {number} Customer count
   */
  getCustomerCount() {
    return this.storageService.count();
  }

  /**
   * Import customers from JSON
   * @param {string} jsonData - JSON string of customer data
   * @param {boolean} [merge=false] - Whether to merge with existing data
   * @returns {boolean} Whether import was successful
   */
  importCustomers(jsonData, merge = false) {
    return this.storageService.importData(jsonData, merge);
  }

  /**
   * Export customers as JSON
   * @returns {string} JSON string of customer data
   */
  exportCustomers() {
    return this.storageService.exportData();
  }

  /**
   * Create a backup of customer data
   * @returns {Object} Backup result
   */
  backupCustomers() {
    return this.storageService.backup('Manual customer data backup');
  }
}

// Create singleton instance
const customerService = new CustomerService();

export default customerService;